import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import authService from '../../services/authService';
import { useAuth } from '../../context/AuthContext';
import { enqueueSnackbar } from 'notistack';

/**
 * Component to handle magic link authentication from email
 * Checks for magic_token in URL query params and authenticates the user
 */
const MagicLinkHandler = ({ children }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const { checkAuthStatus, setIsProcessingMagicToken } = useAuth();
    const [isProcessing, setIsProcessing] = useState(false);

    useEffect(() => {
        const handleMagicLogin = async () => {
            // Parse query parameters
            const searchParams = new URLSearchParams(location.search);
            const magicToken = searchParams.get('magic_token');

            // If no magic token, skip processing
            if (!magicToken) {
                setIsProcessingMagicToken(false); // Ensure flag is false
                return;
            }

            // Prevent multiple simultaneous processing
            if (isProcessing) {
                return;
            }

            setIsProcessing(true);
            setIsProcessingMagicToken(true); // Signal to ProtectedRoute to wait

            try {
                // Call magic login API
                const response = await authService.magicLogin(magicToken);

                if (response.success) {
                    // Refresh auth context
                    checkAuthStatus();

                    // Show success message
                    enqueueSnackbar('Successfully authenticated! Welcome back.', {
                        variant: 'success',
                        autoHideDuration: 3000,
                        anchorOrigin: {
                            vertical: 'top',
                            horizontal: 'right',
                        },
                    });

                    // Remove magic_token from URL to clean it up
                    searchParams.delete('magic_token');
                    const newSearch = searchParams.toString();
                    const newUrl = `${location.pathname}${newSearch ? `?${newSearch}` : ''}`;

                    // Replace the URL without the magic_token
                    navigate(newUrl, { replace: true });
                } else {
                    throw new Error(response.message || 'Authentication failed');
                }
            } catch (error) {
                console.error('Magic login failed:', error);

                // Show error message
                const errorMessage = error.response?.data?.message ||
                    error.message ||
                    'Authentication failed. Please try logging in manually.';

                enqueueSnackbar(errorMessage, {
                    variant: 'error',
                    autoHideDuration: 5000,
                    anchorOrigin: {
                        vertical: 'top',
                        horizontal: 'right',
                    },
                });

                // Remove magic_token from URL even on failure
                searchParams.delete('magic_token');
                const newSearch = searchParams.toString();
                const newUrl = `${location.pathname}${newSearch ? `?${newSearch}` : ''}`;

                // Navigate to the same page without the token
                navigate(newUrl, { replace: true });
            } finally {
                setIsProcessing(false);
                setIsProcessingMagicToken(false); // Signal to ProtectedRoute that processing is done
            }
        };

        handleMagicLogin();
    }, [location.search]); // Only re-run when search params change

    return children;
};

export default MagicLinkHandler;
